import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isStudioSuperAdmin, readAuthUser, trialEndsAtMs } from "../../helpers/studioModules";
import "./studio-trial-timer.scss";

/** 6d 04:12:09 — fixed-width digits so the chip never jiggles as it counts. */
export const formatTrialLeft = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d > 0 ? `${d}d ` : ""}${pad(h)}:${pad(m)}:${pad(s)}`;
};

/**
 * The studio trial as a small running countdown in the header. The full message shows on hover
 * (mouse) or on tap (phone), instead of a banner on every page.
 */
const StudioTrialTimer = () => {
  const [authUser] = useState(readAuthUser());
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const ends = trialEndsAtMs(authUser);
  const active = ends > now;

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!authUser?.users || isStudioSuperAdmin(authUser) || !ends) return null;
  const role = authUser?.users?.role;
  if (role !== "ORGANIZATION_ADMIN" && role !== "ORGANIZATION_MANAGER") return null;

  const days = Math.ceil((ends - now) / 86400000);
  return (
    <div ref={ref} className={`studio-trial-timer${active ? "" : " is-ended"}${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="studio-trial-timer__chip"
        aria-expanded={open}
        aria-describedby="studio-trial-timer-tip"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
          <path d="M12 9v4l2.5 1.5M9.5 2.5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="studio-trial-timer__label">{active ? "Trial" : "Trial ended"}</span>
        {active ? <span className="studio-trial-timer__time">{formatTrialLeft(ends - now)}</span> : null}
      </button>
      <div id="studio-trial-timer-tip" role="tooltip" className="studio-trial-timer__tip">
        {active ? (
          <>
            All Studio modules are on for {days} more day{days === 1 ? "" : "s"}. Choose what to keep in{" "}
            <Link to="/studio/modules" onClick={() => setOpen(false)}>
              Profile → Studio modules
            </Link>
            .
          </>
        ) : (
          <>
            Trial ended. Keep or turn off modules in{" "}
            <Link to="/studio/modules" onClick={() => setOpen(false)}>
              Profile → Studio modules
            </Link>
            .
          </>
        )}
      </div>
    </div>
  );
};

export default StudioTrialTimer;
